import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  UserCredential,
  User,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult as firebaseGetRedirectResult,
  browserPopupRedirectResolver,
  browserLocalPersistence,
  setPersistence,
  reload
} from 'firebase/auth';
import { auth } from './firebase';
import { clearAnonymousSession } from '../utils/anonymousSession';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

// Create a debug flag to show detailed logs
const DEBUG = true;

// Initialize persistence to avoid cross-domain issues
setPersistence(auth, browserLocalPersistence)
  .then(() => {
    console.log("Auth persistence set to browserLocalPersistence");
  })
  .catch((error) => {
    console.error("Error setting persistence:", error);
  });

export const authService = {
  // Debug log helper
  _log: (message: string, ...args: any[]) => {
    if (DEBUG) {
      console.log(`[Auth Service] ${message}`, ...args);
    }
  },
  
  // Register a new user
  register: async (email: string, password: string): Promise<UserCredential> => {
    try {
      authService._log('Registering new user:', email);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      authService._log('User registered successfully:', result.user.uid);
      
      // Clear anonymous session when user registers
      clearAnonymousSession();
      authService._log('Cleared anonymous session after registration');
      
      return result;
    } catch (error) {
      authService._log('Registration error:', error);
      throw error;
    }
  },
  
  // Sign in existing user
  login: async (email: string, password: string): Promise<UserCredential> => {
    try {
      authService._log('Logging in user:', email);
      const result = await signInWithEmailAndPassword(auth, email, password);
      authService._log('User logged in successfully:', result.user.uid);
      
      // Clear anonymous session when user logs in
      clearAnonymousSession();
      authService._log('Cleared anonymous session after login');
      
      return result;
    } catch (error) {
      authService._log('Login error:', error);
      throw error;
    }
  },
  
  // Sign in with Google - try popup first, fallback to redirect
  loginWithGoogle: async (): Promise<UserCredential | null> => {
    try {
      authService._log('Starting Google sign-in');
      const provider = new GoogleAuthProvider();
      
      // Configure Google provider for better compatibility
      provider.setCustomParameters({
        prompt: 'select_account',
        login_hint: 'Use your Google account',
      });
      
      try {
        // Try popup method first (works better on most platforms)
        authService._log('Attempting Google sign-in with popup');
        const result = await signInWithPopup(auth, provider);
        authService._log('Google sign-in successful', result.user.uid);
        
        // Clear anonymous session when user logs in with Google
        clearAnonymousSession();
        authService._log('Cleared anonymous session after Google login');
        
        return result;
      } catch (popupError: any) {
        // Log the specific error
        authService._log('Popup sign-in failed:', popupError);
        
        // If popup fails with CORS or is blocked, try redirect as fallback
        if (
          popupError.code === 'auth/popup-blocked' || 
          popupError.code === 'auth/popup-closed-by-user' ||
          popupError.code?.includes('cross-origin') ||
          popupError.message?.includes('cross-origin')
        ) {
          authService._log('Falling back to redirect method');
          // Use redirect as fallback (will complete on page reload)
          await signInWithRedirect(auth, provider);
          return null; // Return null as the result will be handled on page reload
        }
        
        // If it's another type of error, rethrow it
        throw popupError;
      }
    } catch (error) {
      authService._log('Google sign-in error:', error);
      throw error;
    }
  },
  
  // Get redirect result after Google sign-in
  getRedirectResult: async (): Promise<UserCredential | null> => {
    try {
      authService._log('Getting redirect result');
      const result = await firebaseGetRedirectResult(auth);
      
      if (result?.user) {
        authService._log('Redirect result successful:', result.user.uid);
        
        // Clear anonymous session when user completes Google sign-in via redirect
        clearAnonymousSession();
        authService._log('Cleared anonymous session after Google redirect login');
      } else {
        authService._log('No redirect result');
      }
      
      return result;
    } catch (error) {
      authService._log('Redirect result error:', error);
      throw error;
    }
  },
  
  // Sign out
  logout: async (): Promise<void> => {
    try {
      authService._log('Signing out user');
      await firebaseSignOut(auth);
      authService._log('User signed out successfully');
    } catch (error) {
      authService._log('Logout error:', error);
      throw error;
    }
  },
  
  // Reset password
  resetPassword: async (email: string): Promise<void> => {
    try {
      authService._log('Sending password reset email to:', email);
      await sendPasswordResetEmail(auth, email);
      authService._log('Password reset email sent successfully');
    } catch (error) {
      authService._log('Password reset error:', error);
      throw error;
    }
  },
  
  // Update user profile
  updateProfile: async (displayName?: string, photoURL?: string): Promise<void> => {
    try {
      if (!auth.currentUser) {
        authService._log('No user is signed in');
        throw new Error('No user is signed in');
      }
      
      authService._log('Updating profile for user:', auth.currentUser.uid);
      authService._log('New profile values - displayName:', displayName, 'photoURL:', photoURL);
      
      // Special handling for photoURL
      const isRemovingPhoto = photoURL === '';
      
      // Create update object with proper handling of empty values
      const updateData: { displayName?: string | null; photoURL?: string | null } = {};
      
      // Only update displayName if provided
      if (displayName !== undefined) {
        updateData.displayName = displayName;
      }
      
      // Handle photoURL - explicitly set to null if empty string is provided
      if (photoURL !== undefined) {
        if (isRemovingPhoto) {
          authService._log('Explicitly removing profile photo');
          updateData.photoURL = null;
        } else {
          updateData.photoURL = photoURL;
        }
      }
      
      authService._log('Update data being sent:', updateData);
      await updateProfile(auth.currentUser, updateData);
      
      // Force reload of user data after profile update
      await authService.refreshUser();
      
      // Double-check if photo was properly removed
      if (isRemovingPhoto && auth.currentUser.photoURL) {
        authService._log('Photo URL still present after removal attempt, trying again with direct null');
        await updateProfile(auth.currentUser, { photoURL: null });
        await authService.refreshUser();
      }
      
      authService._log('Profile updated successfully, current values:', {
        displayName: auth.currentUser.displayName,
        photoURL: auth.currentUser.photoURL
      });
    } catch (error) {
      authService._log('Profile update error:', error);
      throw error;
    }
  },
  
  // Force reload the current user's data from Firebase servers
  refreshUser: async (): Promise<void> => {
    try {
      if (!auth.currentUser) {
        authService._log('No user is signed in');
        return;
      }
      
      authService._log('Refreshing user data from server');
      await reload(auth.currentUser);
      authService._log('User data refreshed successfully');
    } catch (error) {
      authService._log('Error refreshing user data:', error);
      throw error;
    }
  },
  
  // Get current user
  getCurrentUser: (): User | null => {
    const user = auth.currentUser;
    authService._log('Get current user:', user ? user.uid : 'No user signed in');
    return user;
  },
  
  // Listen to auth state changes
  onAuthStateChange: (callback: (user: AuthUser | null) => void) => {
    authService._log('Setting up auth state change listener');
    
    return onAuthStateChanged(auth, 
      (user) => {
        authService._log('Auth state changed:', user ? `User: ${user.email}` : 'No user');
        if (user) {
          // Check if photoURL is empty string and convert to null
          // This is a safeguard for potential Firebase edge cases
          const photoURL = user.photoURL === '' ? null : user.photoURL;
          
          const authUser: AuthUser = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: photoURL
          };
          
          authService._log('Auth user processed:', authUser);
          callback(authUser);
        } else {
          callback(null);
        }
      }, 
      (error) => {
        authService._log('Auth state change error:', error);
        // Still call callback with null to prevent UI from getting stuck
        callback(null);
      }
    );
  }
}; 